import React, { useState } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import Offcanvas from 'react-bootstrap/Offcanvas';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import AsyncSelect from 'react-select/async';
import { PartialUser, TimesheetLunchBreak } from '../types';
import { postLunchBreak, updateLunchBreak } from '../api/dataService';

interface AddLunchBreakModalProps {
    show: boolean;
    onHide: () => void;
    employeeId: string | number;
    defaultDate: Date;
    lunchBreakToEdit?: TimesheetLunchBreak | null;
    onSaved: (lb: TimesheetLunchBreak) => void;
    canEditEmployee: boolean;
    employeeOptions: PartialUser[];
    defaultEmployeeId?: string | number | null;
}

const AddLunchBreakModal: React.FC<AddLunchBreakModalProps> = ({
    show,
    onHide,
    employeeId,
    defaultDate,
    lunchBreakToEdit,
    onSaved,
    canEditEmployee,
    employeeOptions,
    defaultEmployeeId,
}) => {
    const [startTime, setStartTime] = useState<Date | null>(null);
    const [endTime, setEndTime] = useState<Date | null>(null);
    const [description, setDescription] = useState<string>('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | number | ''>('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string>();

    const parseTimeToDate = (timeStr?: string): Date | null => {
        if (!timeStr) return null;
        const [h, m] = timeStr.split(':').map(Number);
        const d = new Date(defaultDate);
        d.setHours(h, m, 0, 0);
        return d;
    };

    const handleShow = () => {
        setStartTime(parseTimeToDate(lunchBreakToEdit?.start_time));
        setEndTime(parseTimeToDate(lunchBreakToEdit?.end_time));
        setDescription(lunchBreakToEdit?.description ?? '');
        if (lunchBreakToEdit?.employee?.id !== undefined && lunchBreakToEdit?.employee?.id !== null) {
            setSelectedEmployeeId(lunchBreakToEdit.employee.id);
        } else if (canEditEmployee) {
            setSelectedEmployeeId(
                defaultEmployeeId !== undefined && defaultEmployeeId !== null ? defaultEmployeeId : ''
            );
        } else {
            setSelectedEmployeeId(employeeId ?? '');
        }
        setError(undefined);
    };

    const startMins = startTime ? startTime.getHours() * 60 + startTime.getMinutes() : null;
    const endMins = endTime ? endTime.getHours() * 60 + endTime.getMinutes() : null;
    const hasEmployee = canEditEmployee ? !!selectedEmployeeId : true;
    const isValid = startMins !== null && endMins !== null && endMins > startMins && hasEmployee;

    const handleSave = async () => {
        if (!startTime || !endTime) return;
        setIsSaving(true);
        setError(undefined);

        const dateStr = format(defaultDate, 'yyyy-MM-dd');
        const startTimeStr = format(startTime, 'HH:mm:ss');
        const endTimeStr = format(endTime, 'HH:mm:ss');

        try {
            let result: any;
            if (lunchBreakToEdit?.id) {
                result = await updateLunchBreak(String(lunchBreakToEdit.id), {
                    start_time: startTimeStr,
                    end_time: endTimeStr,
                    description: description || undefined,
                });
            } else {
                const employeeToSave = canEditEmployee ? selectedEmployeeId : employeeId;
                if (!employeeToSave) {
                    setError('Please select an employee.');
                    setIsSaving(false);
                    return;
                }
                result = await postLunchBreak({
                    employee: employeeToSave as any,
                    date: dateStr,
                    start_time: startTimeStr,
                    end_time: endTimeStr,
                    description: description || undefined,
                    status: 'open',
                });
            }

            const savedBreak = (result && (result.data ?? result)) as TimesheetLunchBreak | null;
            if (savedBreak && !savedBreak.employee && lunchBreakToEdit?.employee) {
                savedBreak.employee = lunchBreakToEdit.employee;
            }
            if (savedBreak && !savedBreak.employee && selectedEmployeeId) {
                const matchedEmployee = employeeOptions.find(
                    (u) => String(u.id) === String(selectedEmployeeId)
                );
                if (matchedEmployee) {
                    savedBreak.employee = matchedEmployee;
                }
            }
            if (savedBreak && savedBreak.id) {
                onSaved(savedBreak);
                onHide();
            } else {
                setError('Failed to save break. Please try again.');
            }
        } catch (e) {
            setError('An error occurred. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Offcanvas show={show} onHide={onHide} onShow={handleShow} placement="end">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{lunchBreakToEdit?.id ? 'Edit Break' : 'Add Break'}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <Form>
                    {canEditEmployee && (
                        <Row className="mb-3">
                            <Col xs={12}>
                                <Form.Label className="fw-bold">
                                    Employee <span className="text-danger">*</span>
                                </Form.Label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions={employeeOptions.map((user) => ({
                                        label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
                                        value: user.id,
                                    }))}
                                    name="employee"
                                    placeholder="Search employee..."
                                    loadOptions={(inputValue) =>
                                        Promise.resolve(
                                            employeeOptions
                                                .map((user) => ({
                                                    label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
                                                    value: user.id,
                                                }))
                                                .filter((option) =>
                                                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                                                )
                                        )
                                    }
                                    isLoading={!employeeOptions.length}
                                    value={(() => {
                                        const employee = employeeOptions.find(
                                            (u) => String(u.id) === String(selectedEmployeeId)
                                        );
                                        return employee
                                            ? {
                                                label: `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim(),
                                                value: employee.id,
                                            }
                                            : null;
                                    })()}
                                    onChange={(selectedOption) => {
                                        setSelectedEmployeeId(selectedOption?.value ?? '');
                                    }}
                                    isDisabled={!!lunchBreakToEdit?.employee}
                                />
                            </Col>
                        </Row>
                    )}
                    <Row className="mb-4 justify-content-center">
                        <Col xs={12} md={5} lg={4} className="mb-3 mb-md-0">
                            <Form.Group controlId="break_start_time" className="text-center">
                                <Form.Label className="fw-bold d-block">
                                    Start Time <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="w-100">
                                    <DatePicker
                                        className="form-control text-center"
                                        selected={startTime}
                                        onChange={(t: Date | null) => {
                                            setStartTime(t);
                                            if (t && endTime) {
                                                const newStartMins = t.getHours() * 60 + t.getMinutes();
                                                const currentEndMins = endTime.getHours() * 60 + endTime.getMinutes();
                                                if (currentEndMins <= newStartMins) setEndTime(null);
                                            }
                                        }}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        dateFormat="h:mm aa"
                                        placeholderText="Start time"
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={5} lg={4}>
                            <Form.Group controlId="break_end_time" className="text-center">
                                <Form.Label className="fw-bold d-block">
                                    End Time <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="w-100">
                                    <DatePicker
                                        className="form-control text-center"
                                        selected={endTime}
                                        onChange={(t: Date | null) => setEndTime(t)}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeIntervals={15}
                                        dateFormat="h:mm aa"
                                        placeholderText="End time"
                                        disabled={!startTime}
                                        filterTime={(time: Date) => {
                                            if (!startTime) return false;
                                            const candidateMins = time.getHours() * 60 + time.getMinutes();
                                            const startMinsVal = startTime.getHours() * 60 + startTime.getMinutes();
                                            return candidateMins > startMinsVal;
                                        }}
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3 justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <Form.Group controlId="break_notes">
                                <Form.Label className="fw-bold">Notes:</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    style={{ height: '100px' }}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Add notes here (optional)"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col xs={12} sm="auto" className="d-grid">
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                disabled={isSaving || !isValid}
                            >
                                {isSaving ? 'Saving…' : 'Save'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AddLunchBreakModal;
